/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap,
  addEdge, useNodesState, useEdgesState, useReactFlow, Handle, Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Input, Select, InputNumber, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import EmailBodyEditor from '../../EmailCampaigns/Builder/EmailBodyEditor';
import {
  SequenceLayout, Palette, PaletteItem, Canvas, ConfigPanel
} from './styled';

const NODE_DEFINITIONS = {
  start: { label: 'Start', icon: '⚡', group: 'TRIGGER', desc: 'Entry point', single: true },
  sendEmail: { label: 'Send Email', icon: '✉️', group: 'ACTIONS', desc: 'Send a templated email' },
  sendSms: { label: 'Send SMS', icon: '💬', group: 'ACTIONS', desc: 'Send a text message' },
  agentCall: { label: 'Agent Call', icon: '📞', group: 'ACTIONS', desc: 'AI voice call', soon: true },
  webhook: { label: 'Webhook', icon: '🌐', group: 'ACTIONS', desc: 'Call external URL', soon: true },
  internalNote: { label: 'Internal Note', icon: '📝', group: 'ACTIONS', desc: 'Add a contact note', soon: true },
  assign: { label: 'Assign', icon: '👤', group: 'ACTIONS', desc: 'Assign to a teammate', soon: true },
  updateContact: { label: 'Update Contact', icon: '🏷️', group: 'ACTIONS', desc: 'Update fields/tags', soon: true },
  createTask: { label: 'Create Task', icon: '✅', group: 'ACTIONS', desc: 'Create a follow-up task', soon: true },
  wait: { label: 'Wait', icon: '⏱️', group: 'CONTROL', desc: 'Pause for a duration' },
  condition: { label: 'Condition', icon: '🔀', group: 'CONTROL', desc: 'If / else split' },
  branch: { label: 'Branch', icon: '🌿', group: 'CONTROL', desc: 'Same as condition', soon: true },
  goalReached: { label: 'Goal Reached', icon: '🎯', group: 'EXIT', desc: 'Mark goal complete' },
  end: { label: 'End', icon: '⛔', group: 'EXIT', desc: 'Stop the sequence' }
};

const GROUPS = ['TRIGGER', 'ACTIONS', 'CONTROL', 'EXIT'];

const mkId = (prefix) => `${prefix}_${Math.random().toString(36).slice(2, 8)}`;

/* --- Custom node renderers ------------------------------------------------ */

const labelFor = (node) => {
  const def = NODE_DEFINITIONS[node.type];
  if (node.data?.label) return node.data.label;
  return def?.label || node.type;
};

const subtitleFor = (node) => {
  switch (node.type) {
    case 'sendEmail': return node.data?.subject || 'No subject set';
    case 'sendSms': return node.data?.message || 'No message set';
    case 'wait': return `${node.data?.duration || 0} ${node.data?.unit || 'minutes'}`;
    case 'condition':
    case 'branch':
      return node.data?.field
        ? `if ${node.data.field} ${node.data.operator || 'equals'} ${node.data.value || ''}`
        : 'No condition set';
    default: return NODE_DEFINITIONS[node.type]?.desc || '';
  }
};

const GenericNode = ({ data, type }) => {
  const def = NODE_DEFINITIONS[type] || {};
  return (
    <>
      {type !== 'start' && <Handle type="target" position={Position.Left} />}
      <div className="node-title"><span>{def.icon}</span><span>{labelFor({ type, data })}</span></div>
      <div className="node-sub">{subtitleFor({ type, data })}</div>
      {type === 'condition' || type === 'branch' ? (
        <>
          <Handle id="true" type="source" position={Position.Right} style={{ top: '35%' }} />
          <Handle id="false" type="source" position={Position.Right} style={{ top: '70%' }} />
        </>
      ) : (
        type !== 'end' && type !== 'goalReached' && <Handle type="source" position={Position.Right} />
      )}
    </>
  );
};

const NODE_TYPES = Object.keys(NODE_DEFINITIONS).reduce((acc, key) => {
  acc[key] = (props) => <GenericNode {...props} type={key} />;
  return acc;
}, {});

/* --- Inner builder (must be inside ReactFlowProvider) -------------------- */

const SequenceInner = ({ automation, update, templates, verifiedSenders, surveys }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(automation.nodes || []);
  const [edges, setEdges, onEdgesChange] = useEdgesState(automation.edges || []);
  const [selectedId, setSelectedId] = useState(null);
  const wrapperRef = useRef(null);
  const { screenToFlowPosition } = useReactFlow();

  // Sync local state -> parent on every change so wizard navigation persists.
  useEffect(() => { update({ nodes, edges }); }, [nodes, edges]);

  // If parent replaces the graph (e.g. template applied), refresh local state.
  const sigRef = useRef('');
  useEffect(() => {
    const sig = JSON.stringify({ n: automation.nodes?.map((x) => x.id), e: automation.edges?.map((x) => x.id) });
    if (sig !== sigRef.current) {
      sigRef.current = sig;
      setNodes(automation.nodes || []);
      setEdges(automation.edges || []);
    }
  }, [automation.nodes, automation.edges]);

  const onConnect = useCallback((c) => setEdges((eds) => addEdge({ ...c, id: mkId('e') }, eds)), []);

  const onDragStart = (e, type) => {
    e.dataTransfer.setData('application/bha-node', type);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('application/bha-node');
    if (!type || !NODE_DEFINITIONS[type]) return;
    if (NODE_DEFINITIONS[type].single && nodes.some((n) => n.type === type)) return;

    const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
    const newNode = { id: mkId(type), type, position, data: {} };
    if (type === 'wait') newNode.data = { duration: 1, unit: 'hours' };
    if (type === 'sendEmail') newNode.data = { subject: '', html: '', recipientField: '' };
    if (type === 'sendSms') newNode.data = { message: '', recipientField: '' };
    setNodes((ns) => ns.concat(newNode));
  }, [nodes, screenToFlowPosition]);

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };

  const selected = useMemo(() => nodes.find((n) => n.id === selectedId), [nodes, selectedId]);

  const updateSelectedData = (patch) => {
    setNodes((ns) => ns.map((n) => (n.id === selectedId ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const deleteSelected = () => {
    if (!selected || selected.type === 'start') return;
    setNodes((ns) => ns.filter((n) => n.id !== selectedId));
    setEdges((es) => es.filter((e) => e.source !== selectedId && e.target !== selectedId));
    setSelectedId(null);
  };

  return (
    <SequenceLayout>
      <Palette>
        {GROUPS.map((g) => (
          <div key={g}>
            <h4>{g}</h4>
            {Object.entries(NODE_DEFINITIONS)
              .filter(([, def]) => def.group === g)
              .map(([key, def]) => (
                <PaletteItem
                  key={key}
                  draggable
                  onDragStart={(e) => onDragStart(e, key)}
                >
                  <span className="icon">{def.icon}</span>
                  <span>{def.label}</span>
                  {def.soon && <span className="badge">Soon</span>}
                </PaletteItem>
              ))}
          </div>
        ))}
      </Palette>

      <Canvas ref={wrapperRef} onDrop={onDrop} onDragOver={onDragOver}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={(_, n) => setSelectedId(n.id)}
          onPaneClick={() => setSelectedId(null)}
          nodeTypes={NODE_TYPES}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
        >
          <Background gap={20} />
          <Controls />
          <MiniMap pannable zoomable />
        </ReactFlow>
      </Canvas>

      <ConfigPanel>
        {!selected && <div className="empty">Select a node to configure it, or drag a new one from the left palette.</div>}
        {selected && (
          <NodeConfig
            node={selected}
            updateData={updateSelectedData}
            onDelete={deleteSelected}
            templates={templates}
            verifiedSenders={verifiedSenders}
            surveys={surveys}
            triggerEvent={automation.triggerEvent}
          />
        )}
      </ConfigPanel>
    </SequenceLayout>
  );
};

const NodeConfig = ({ node, updateData, onDelete, templates, verifiedSenders, surveys, triggerEvent }) => {
  const def = NODE_DEFINITIONS[node.type] || {};
  return (
    <>
      <h3>{def.icon} {def.label}</h3>
      <div className="sub">{def.desc}</div>

      <label>Display label</label>
      <Input
        value={node.data?.label || ''}
        placeholder={def.label}
        onChange={(e) => updateData({ label: e.target.value })}
      />

      {node.type === 'start' && (
        <>
          <label>Trigger event</label>
          <Input value={triggerEvent || '(not set — go to step 2)'} disabled />
        </>
      )}

      {node.type === 'sendEmail' && (
        <>
          <label>Use a saved template (optional)</label>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder="Choose a template…"
            value={node.data?.templateId || undefined}
            onChange={(v) => {
              const tpl = templates.find((t) => t._id === v);
              updateData({
                templateId: v || null,
                subject: tpl?.subject || node.data?.subject || '',
                html: tpl?.html || node.data?.html || ''
              });
            }}
            options={templates.map((t) => ({ value: t._id, label: t.name }))}
          />

          <label>Subject</label>
          <Input
            value={node.data?.subject || ''}
            onChange={(e) => updateData({ subject: e.target.value })}
            placeholder="Hi {{firstName}}, welcome…"
          />

          <label>Body</label>
          <EmailBodyEditor
            value={node.data?.html || ''}
            onChange={(v) => updateData({ html: v })}
          />

          <label>Recipient field</label>
          <Input
            value={node.data?.recipientField || ''}
            onChange={(e) => updateData({ recipientField: e.target.value })}
            placeholder="email (default) or a fixed address like staff@example.org"
          />

          <label>From name</label>
          <Input
            value={node.data?.fromName || ''}
            onChange={(e) => updateData({ fromName: e.target.value })}
            placeholder="Black Heart Association"
          />

          <label>From email</label>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder="Default sender"
            value={node.data?.fromEmail || undefined}
            onChange={(v) => updateData({ fromEmail: v || '' })}
            options={(verifiedSenders || []).map((s) => ({
              value: s.from_email || s.fromEmail || s.email,
              label: `${s.from_name || s.fromName || ''} <${s.from_email || s.fromEmail || s.email}>`
            }))}
          />

          <label>Reply-to</label>
          <Input
            value={node.data?.replyTo || ''}
            onChange={(e) => updateData({ replyTo: e.target.value })}
            placeholder="reply@blackheartassociation.org"
          />

          <label>Link a survey (optional)</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder="Choose a survey…"
            value={node.data?.surveyId || undefined}
            onChange={(v) => updateData({ surveyId: v || null })}
            options={(surveys || []).map((s) => ({ value: s._id, label: s.title }))}
          />
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            When set, use the <code>{'{{surveyUrl}}'}</code> tag in the subject or body to insert this survey&apos;s link.
          </div>
        </>
      )}

      {node.type === 'sendSms' && (
        <>
          <label>Message</label>
          <Input.TextArea
            rows={4}
            value={node.data?.message || ''}
            onChange={(e) => updateData({ message: e.target.value })}
            placeholder="Hi {{firstName}}, thanks for your screening! Tell us how we did: {{surveyUrl}}"
            maxLength={1000}
            showCount
          />

          <label>Recipient field</label>
          <Input
            value={node.data?.recipientField || ''}
            onChange={(e) => updateData({ recipientField: e.target.value })}
            placeholder="phoneNumber (default)"
          />

          <label>Link a survey (optional)</label>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder="Choose a survey…"
            value={node.data?.surveyId || undefined}
            onChange={(v) => updateData({ surveyId: v || null })}
            options={(surveys || []).map((s) => ({ value: s._id, label: s.title }))}
          />
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            When set, use the <code>{'{{surveyUrl}}'}</code> tag in the message to insert this survey&apos;s link.
            Available tokens depend on the trigger (e.g. <code>{'{{firstName}}'}</code>).
          </div>
        </>
      )}

      {node.type === 'wait' && (
        <>
          <label>Duration</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <InputNumber
              min={0}
              value={node.data?.duration ?? 1}
              onChange={(v) => updateData({ duration: v })}
              style={{ flex: 1 }}
            />
            <Select
              value={node.data?.unit || 'hours'}
              onChange={(v) => updateData({ unit: v })}
              options={[
                { value: 'minutes', label: 'minutes' },
                { value: 'hours', label: 'hours' },
                { value: 'days', label: 'days' }
              ]}
              style={{ width: 130 }}
            />
          </div>
          <div className="coming">The sequence pauses here and resumes automatically after the wait elapses. Test fires skip waits so you can preview the full flow immediately.</div>
        </>
      )}

      {(node.type === 'condition' || node.type === 'branch') && (
        <>
          <label>Field</label>
          <Input
            value={node.data?.field || ''}
            onChange={(e) => updateData({ field: e.target.value })}
            placeholder="e.g. preferredCity"
          />
          <label>Operator</label>
          <Select
            style={{ width: '100%' }}
            value={node.data?.operator || 'equals'}
            onChange={(v) => updateData({ operator: v })}
            options={[
              { value: 'equals', label: 'equals' },
              { value: 'not_equals', label: 'does not equal' },
              { value: 'contains', label: 'contains' },
              { value: 'exists', label: 'is provided' }
            ]}
          />
          <label>Value</label>
          <Input
            value={node.data?.value || ''}
            disabled={node.data?.operator === 'exists'}
            onChange={(e) => updateData({ value: e.target.value })}
          />
        </>
      )}

      {def.soon && <div className="coming">This node type is reserved for an upcoming release. It will be saved with the sequence but skipped at runtime.</div>}

      {node.type !== 'start' && (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={onDelete}
          style={{ marginTop: 24, width: '100%' }}
        >
          Delete node
        </Button>
      )}
    </>
  );
};

const StepSequence = (props) => (
  <ReactFlowProvider>
    <SequenceInner {...props} />
  </ReactFlowProvider>
);

export default StepSequence;
